import { useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  navigateToCommunity,
  navigateToMember,
  navigateToSite,
} from '@flexo/resolvers';
import { useTranslation } from 'react-i18next';
import { resolveMemberForSite } from '@flexo/general';
import * as luxon from 'luxon';

type HeaderEntityType = 'site' | 'community';

interface UseHeaderEntitiesOptions {
  forceEntityType?: HeaderEntityType;
  selectedIdOverride?: string | null;
}

interface UseHeaderEntitiesResult<T> {
  entityType: HeaderEntityType;
  items: T[];
  selected: T | null;
  getLabel: (item: T) => string;
  getId: (item: T) => string;
  getDescription: (item: T) => string;
  onSelect: (item: T) => void;
  disabled: boolean;
  singleItem: boolean;
  noOptionsText: string;
}

export function useHeaderEntities<T = any>(
  options?: UseHeaderEntitiesOptions
): UseHeaderEntitiesResult<T> {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];
  const metadata = useSelector((state: any) => state.metadata);
  const calendar = useSelector((state: any) => state.calendar);
  const user = useSelector((state: any) => state.user);
  const isEndUser =
    (user?.orgRole || user?.tokenContent?.org_role) === 'org:end_user';
  const selectedDateFrom = calendar?.hiveCalendar?.selectedDate?.[0]
    ? luxon.DateTime.fromJSDate(calendar.hiveCalendar.selectedDate[0]).toFormat(
        'yyyy-MM-dd'
      )
    : luxon.DateTime.now().toFormat('yyyy-MM-dd');

  const entityType: HeaderEntityType = useMemo(() => {
    if (options?.forceEntityType) return options.forceEntityType;
    // community* (web data-vis + overview) and the mobile 'community' route.
    if (pathSegment.startsWith('community')) return 'community';
    // site/household (mobile) + site*/member* (web) all pick from sites.
    return 'site';
  }, [pathSegment, options?.forceEntityType]);

  const items = useMemo<T[]>(() => {
    if (entityType === 'site') {
      const allSites = ((metadata?.entities as any)?.site as any[]) || [];
      const allMembers = ((metadata?.entities as any)?.member as any[]) || [];
      const urlId = params?.id as string | undefined;
      const selectedEntity = (metadata?.selectedEntity as any) || null;
      const resolvedMemberId =
        pathSegment.startsWith('member') && urlId
          ? urlId
          : resolveMemberForSite(
              allMembers,
              urlId || selectedEntity?.siteId || '',
              selectedDateFrom
            ) || selectedEntity?.memberId;

      if (!isEndUser || !resolvedMemberId) {
        return allSites as T[];
      }

      const memberSites = allSites.filter((site: any) => {
        const siteMemberId =
          resolveMemberForSite(allMembers, site?.siteId, selectedDateFrom) ||
          site?.memberId;
        return siteMemberId === resolvedMemberId;
      });

      if (memberSites.length <= 1) {
        return memberSites as T[];
      }

      return [
        {
          __allSites: true,
          siteId: '__all_sites__',
          siteName: t('dropdowns.allSites') as string,
          pod: '',
          memberId: resolvedMemberId,
        },
        ...memberSites,
      ] as T[];
    }
    return (((metadata?.entities as any)?.community as T[]) || []) as T[];
  }, [
    entityType,
    isEndUser,
    metadata,
    params?.id,
    pathSegment,
    selectedDateFrom,
    t,
  ]);

  const getLabel = useCallback(
    (item: any) =>
      entityType === 'site' ? item?.siteName || '' : item?.communityName || '',
    [entityType]
  );

  const getId = useCallback(
    (item: any) =>
      entityType === 'site' ? item?.siteId || '' : item?.communityId || '',
    [entityType]
  );

  const getDescription = useCallback(
    (item: any) =>
      item?.__allSites
        ? ''
        : entityType === 'site'
          ? item?.pod || ''
          : item?.communityExternalId || '',
    [entityType]
  );

  const selected = useMemo<T | null>(() => {
    if (!items?.length) return null;
    const urlId = params?.id as string | undefined;
    const allSitesOption = (items as any[])?.find((it) => it?.__allSites) as
      | T
      | undefined;
    if (
      entityType === 'site' &&
      pathSegment.startsWith('member') &&
      allSitesOption
    ) {
      return allSitesOption;
    }
    // Honor URL param only when not forcing a specific entity type
    if (!options?.forceEntityType && urlId) {
      const byUrl = (items as any[])?.find((it) => getId(it) === urlId) as
        | T
        | undefined;
      if (byUrl) return byUrl;
    }
    // Allow caller to override selected item by id (e.g., billing in community view)
    if (options?.selectedIdOverride) {
      const fromOverride = (items as any[])?.find(
        (it) => getId(it) === options.selectedIdOverride
      ) as T | undefined;
      if (fromOverride) return fromOverride;
    }
    const sel = (metadata?.selectedEntity as any) || null;
    if (sel && getId(sel)) return sel as T;
    return (items[0] as T) || null;
  }, [
    entityType,
    items,
    params?.id,
    pathSegment,
    metadata?.selectedEntity,
    getId,
    options?.forceEntityType,
    options?.selectedIdOverride,
  ]);

  const onSelect = useCallback(
    (item: any) => {
      const targetId = getId(item);
      if (!targetId) return;
      if (entityType === 'site') {
        if (item?.__allSites && item?.memberId) {
          navigateToMember('member', item.memberId);
          return;
        }
        // 'household' on the mobile header; 'site' on the web consolidated page.
        navigateToSite(
          pathSegment === 'household' ? 'household' : 'site',
          targetId
        );
      } else {
        navigateToCommunity('community', targetId);
      }
    },
    [entityType, getId, pathSegment]
  );

  const disabled = useMemo(() => items.length === 0, [items]);
  const singleItem = useMemo(() => items.length === 1, [items]);

  const noOptionsText = useMemo(
    () =>
      entityType === 'site'
        ? t('widgets.search.no_results_site')
        : t('widgets.search.no_results'),
    [entityType, t]
  );

  return {
    entityType,
    items,
    selected,
    getLabel: getLabel as (item: T) => string,
    getId: getId as (item: T) => string,
    getDescription: getDescription as (item: T) => string,
    onSelect: onSelect as (item: T) => void,
    disabled,
    singleItem,
    noOptionsText,
  };
}
